import { useState, useEffect, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete using Photon (free OSM-based service via backend proxy).
 * Wraps a Textarea so multi-line addresses still work. Suggestions appear as
 * the user types; clicking one populates the field.
 */
export default function AddressAutocomplete({ value, onChange, placeholder, testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const wrapperRef = useRef();

  // Fetch suggestions when value changes (debounced)
  useEffect(() => {
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/locations/autocomplete", {
          params: { q: value, limit: 5 },
        });
        setSuggestions(res.data?.suggestions || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <Textarea
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[60px]"
      />
      {loading && (
        <Loader2 className="absolute right-2 top-2.5 w-3.5 h-3.5 text-amber-500 animate-spin" />
      )}
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-sm shadow-xl max-h-64 overflow-auto">
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={i}
              data-testid={`address-suggestion-${i}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(s.label);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-900 flex items-start gap-2 border-b border-zinc-800/60 last:border-b-0"
            >
              <MapPin className="w-3.5 h-3.5 text-amber-500/70 mt-0.5 shrink-0" />
              <span className="text-zinc-200">{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
